import type { CurrencyMapValue } from './CurrencyMapField';

/** An account a partner listed under our customer (POST …/discover). */
export interface PartnerAccount {
  uuid: string;
  type: string;
  name: string;
  iban: string;
  bic: string;
  currency: string;
  /** The account customers' money sits on (GuruPay: account_type "virtual_parent"). */
  isMaster: boolean;
}

/**
 * Fill in the client-money and shared accounts from what the partner listed
 * (moved from the GuruPay card to Treasury → Accounts, DEV-139).
 *
 * What counts as the account to use is the partner's rule, not ours, and the
 * API is the one that knows it: at GuruPay it is the account whose
 * account_type is "virtual_parent"; at Bankera every active account is one
 * customers can be paid into, and one account answers for several currencies
 * at once. Either way the currencies come back already attributed, and
 * filling in all of them beats filling in one.
 *
 * A partner may not say which currencies an account is for — Bankera's UAT
 * accounts come back with no balances and no permitted currencies. A single
 * currency-less account then fills every row already asked for and left
 * empty.
 *
 * `stored` is what the server holds for a map and `current` the unsaved
 * edits: both are kept, so a lookup never drops a currency it did not return.
 */
export function fillFromDiscovery(
  accounts: readonly PartnerAccount[],
  stored: (key: string) => CurrencyMapValue,
  current: Readonly<Record<string, CurrencyMapValue>>,
  keys: { readonly masters: boolean; readonly shared: boolean },
): Record<string, CurrencyMapValue> {
  let next: Record<string, CurrencyMapValue> = { ...current };
  const unattributed = accounts.filter((a) => a.isMaster && a.currency === '');
  const only = unattributed.length === 1 ? unattributed[0] : undefined;
  if (keys.shared && only !== undefined && only.iban !== '') {
    const shared = { ...stored('sharedAccounts'), ...(next['sharedAccounts'] ?? {}) };
    let filled = false;
    for (const [currency, entry] of Object.entries(shared)) {
      const iban = typeof entry === 'string' ? entry : (entry.iban ?? '');
      if (iban.trim() === '') {
        shared[currency] = { iban: only.iban, bic: only.bic };
        filled = true;
      }
    }
    if (filled) next = { ...next, sharedAccounts: shared };
  }
  const masters = accounts.filter((a) => a.isMaster && a.currency !== '');
  if (masters.length > 0) {
    next = {
      ...next,
      ...(keys.masters ? {
        masterAccountUuids: {
          ...stored('masterAccountUuids'),
          ...(next['masterAccountUuids'] ?? {}),
          ...Object.fromEntries(masters.map((a) => [a.currency.toUpperCase(), a.uuid])),
        },
      } : {}),
      ...(keys.shared ? {
        sharedAccounts: {
          ...stored('sharedAccounts'),
          ...(next['sharedAccounts'] ?? {}),
          ...Object.fromEntries(masters.filter((a) => a.iban !== '').map((a) => [a.currency.toUpperCase(), { iban: a.iban, bic: a.bic }])),
        },
      } : {}),
    };
  }
  return next;
}
